import React from "react";
import type {
  BadgeOverlayLayer,
  IconOverlayLayer,
  ImageOverlayLayer,
  OverlayLayer,
  ShapeKind,
  ShapeOverlayLayer,
  TextOverlayLayer,
} from "../../api";
import { Tooltip } from "./Tooltip";

type Props = {
  overlay: OverlayLayer;
  /** Patches are merged into the overlay; `kind` is preserved by the Composer. */
  onChange: (uid: string, patch: Partial<OverlayLayer>) => void;
  onDelete?: (uid: string) => void;
  onDuplicate?: (uid: string) => void;
};

/* ============================================================ *
 *  Shared atoms (reused across all kind-specific bodies)        *
 * ============================================================ */

const COLOR_TOKENS: { id: string; label: string; swatch: string }[] = [
  { id: "primary", label: "Primary", swatch: "var(--color-primary, #04b4b4)" },
  { id: "secondary", label: "Secondary", swatch: "var(--color-secondary, #f59e0b)" },
  { id: "accent", label: "Accent", swatch: "var(--color-accent, #fcd34d)" },
  { id: "white", label: "Weiß", swatch: "#ffffff" },
  { id: "black", label: "Schwarz", swatch: "#000000" },
];

const ALIGN_OPTIONS: { id: "left" | "center" | "right"; label: string; icon: string }[] = [
  { id: "left", label: "Links", icon: "⟸" },
  { id: "center", label: "Mitte", icon: "≡" },
  { id: "right", label: "Rechts", icon: "⟹" },
];

const WEIGHT_OPTIONS = [400, 500, 600, 700, 800, 900];

const SHAPE_OPTIONS: { id: ShapeKind; label: string; glyph: string }[] = [
  { id: "rect", label: "Rechteck", glyph: "▭" },
  { id: "pill", label: "Pille", glyph: "⬭" },
  { id: "circle", label: "Kreis", glyph: "●" },
  { id: "star", label: "Stern", glyph: "★" },
  { id: "arrow", label: "Pfeil", glyph: "➜" },
  { id: "triangle", label: "Dreieck", glyph: "▲" },
];

const ICON_OPTIONS: { id: string; glyph: string; label: string }[] = [
  { id: "star", glyph: "★", label: "Stern" },
  { id: "heart", glyph: "♥", label: "Herz" },
  { id: "check", glyph: "✓", label: "Check" },
  { id: "x", glyph: "✕", label: "X" },
  { id: "bolt", glyph: "⚡", label: "Bolt" },
  { id: "sparkles", glyph: "✦", label: "Sparkles" },
  { id: "arrow-right", glyph: "→", label: "Pfeil rechts" },
  { id: "arrow-up", glyph: "↑", label: "Pfeil hoch" },
  { id: "flame", glyph: "🔥", label: "Flame" },
  { id: "crown", glyph: "♛", label: "Krone" },
  { id: "pin", glyph: "📍", label: "Pin" },
  { id: "play", glyph: "▶", label: "Play" },
  { id: "pause", glyph: "⏸", label: "Pause" },
  { id: "shoppingbag", glyph: "🛍", label: "Tasche" },
  { id: "globe", glyph: "🌐", label: "Globus" },
  { id: "euro", glyph: "€", label: "Euro" },
];

/** Color picker compatible with token IDs and arbitrary CSS colors. */
const ColorRow: React.FC<{
  value?: string;
  onChange: (next: string) => void;
  label?: string;
}> = ({ value, onChange, label = "Farbe" }) => {
  const isCustom = !!value && !COLOR_TOKENS.some((c) => c.id === value);
  return (
    <div className="overlay-inspector-section">
      <span className="overlay-inspector-section-label">{label}</span>
      <div className="overlay-color-row">
        {COLOR_TOKENS.map((c) => (
          <Tooltip key={c.id} title={c.label}>
            <button
              className={`overlay-color-chip ${value === c.id ? "active" : ""}`}
              style={{ background: c.swatch }}
              onClick={() => onChange(c.id)}
              data-tip-host="true"
              aria-label={c.label}
            />
          </Tooltip>
        ))}
        <input
          type="color"
          className="overlay-color-picker"
          value={isCustom && value ? value : "#ffffff"}
          onChange={(e) => onChange(e.target.value)}
          title="Eigene Farbe"
        />
      </div>
    </div>
  );
};

/** Common transform fields (X / Y / rotate / opacity). */
const TransformRows: React.FC<{ o: OverlayLayer; set: (p: Partial<OverlayLayer>) => void }> = ({ o, set }) => (
  <>
    <div className="overlay-inspector-row">
      <label className="overlay-inspector-label">
        X (%)
        <input
          type="number"
          min={-10}
          max={110}
          step={0.5}
          value={o.xPct}
          onChange={(e) => set({ xPct: Number(e.target.value) })}
        />
      </label>
      <label className="overlay-inspector-label">
        Y (%)
        <input
          type="number"
          min={-10}
          max={110}
          step={0.5}
          value={o.yPct}
          onChange={(e) => set({ yPct: Number(e.target.value) })}
        />
      </label>
    </div>
    <div className="overlay-inspector-row">
      <label className="overlay-inspector-label">
        Rotation°
        <input
          type="number"
          min={-180}
          max={180}
          step={1}
          value={o.rotate}
          onChange={(e) => set({ rotate: Number(e.target.value) })}
        />
      </label>
      <label className="overlay-inspector-label">
        Opazität
        <input
          type="number"
          min={0}
          max={1}
          step={0.05}
          value={o.opacity}
          onChange={(e) => set({ opacity: Math.max(0, Math.min(1, Number(e.target.value))) })}
        />
      </label>
    </div>
  </>
);

/** Common timing fields (start / duration). */
const TimingRow: React.FC<{ o: OverlayLayer; set: (p: Partial<OverlayLayer>) => void }> = ({ o, set }) => (
  <div className="overlay-inspector-row">
    <label className="overlay-inspector-label">
      Start (s)
      <input
        type="number"
        min={0}
        step={0.1}
        value={o.fromSec}
        onChange={(e) => set({ fromSec: Math.max(0, Number(e.target.value)) })}
      />
    </label>
    <label className="overlay-inspector-label">
      Dauer (s)
      <input
        type="number"
        min={0.2}
        step={0.1}
        value={o.durSec}
        onChange={(e) => set({ durSec: Math.max(0.2, Number(e.target.value)) })}
      />
    </label>
  </div>
);

/* ============================================================ *
 *  Kind-specific bodies                                          *
 * ============================================================ */

const TextBody: React.FC<{ o: TextOverlayLayer; set: (p: Partial<TextOverlayLayer>) => void }> = ({ o, set }) => (
  <>
    <label className="overlay-inspector-label">
      Text
      <textarea
        className="overlay-inspector-textarea"
        value={o.text}
        onChange={(e) => set({ text: e.target.value })}
        rows={3}
        placeholder="Dein Text…"
      />
    </label>
    <div className="overlay-inspector-row">
      <label className="overlay-inspector-label">
        Größe
        <input
          type="number"
          min={12}
          max={400}
          step={2}
          value={o.fontSize}
          onChange={(e) => set({ fontSize: Math.max(12, Number(e.target.value) || 0) })}
        />
      </label>
      <label className="overlay-inspector-label">
        Stärke
        <select value={o.weight} onChange={(e) => set({ weight: Number(e.target.value) })}>
          {WEIGHT_OPTIONS.map((w) => (
            <option key={w} value={w}>
              {w}
            </option>
          ))}
        </select>
      </label>
    </div>
    <ColorRow value={o.color} onChange={(next) => set({ color: next })} />
    <div className="overlay-inspector-section">
      <span className="overlay-inspector-section-label">Ausrichtung</span>
      <div className="overlay-align-row">
        {ALIGN_OPTIONS.map((a) => (
          <button
            key={a.id}
            className={`overlay-align-btn ${o.align === a.id ? "active" : ""}`}
            onClick={() => set({ align: a.id })}
            aria-label={a.label}
            title={a.label}
          >
            {a.icon}
          </button>
        ))}
      </div>
    </div>
    <label className="overlay-inspector-checkbox">
      <input
        type="checkbox"
        checked={!!o.shadow}
        onChange={(e) => set({ shadow: e.target.checked })}
      />
      <span>Schlagschatten (für lesbaren Text auf bewegtem Hintergrund)</span>
    </label>
  </>
);

const ShapeBody: React.FC<{ o: ShapeOverlayLayer; set: (p: Partial<ShapeOverlayLayer>) => void }> = ({ o, set }) => (
  <>
    <div className="overlay-inspector-section">
      <span className="overlay-inspector-section-label">Form</span>
      <div className="overlay-align-row">
        {SHAPE_OPTIONS.map((s) => (
          <button
            key={s.id}
            className={`overlay-align-btn ${o.shape === s.id ? "active" : ""}`}
            onClick={() => set({ shape: s.id })}
            title={s.label}
            aria-label={s.label}
          >
            {s.glyph}
          </button>
        ))}
      </div>
    </div>
    <div className="overlay-inspector-row">
      <label className="overlay-inspector-label">
        Breite (%)
        <input
          type="number"
          min={0.5}
          max={120}
          step={0.5}
          value={o.widthPct}
          onChange={(e) => set({ widthPct: Math.max(0.5, Number(e.target.value)) })}
        />
      </label>
      <label className="overlay-inspector-label">
        Höhe (%)
        <input
          type="number"
          min={0.5}
          max={120}
          step={0.5}
          value={o.heightPct}
          onChange={(e) => set({ heightPct: Math.max(0.5, Number(e.target.value)) })}
        />
      </label>
    </div>
    <ColorRow value={o.fill} onChange={(next) => set({ fill: next })} label="Füllung" />
    <ColorRow value={o.stroke} onChange={(next) => set({ stroke: next })} label="Rahmen" />
    <div className="overlay-inspector-row">
      <label className="overlay-inspector-label">
        Rahmen px
        <input
          type="number"
          min={0}
          max={64}
          step={1}
          value={o.strokeWidth ?? 0}
          onChange={(e) => set({ strokeWidth: Math.max(0, Number(e.target.value)) })}
        />
      </label>
      <label className="overlay-inspector-label">
        Radius
        <input
          type="number"
          min={0}
          max={400}
          step={2}
          value={o.radius ?? 12}
          onChange={(e) => set({ radius: Math.max(0, Number(e.target.value)) })}
          disabled={o.shape !== "rect"}
        />
      </label>
    </div>
  </>
);

const IconBody: React.FC<{ o: IconOverlayLayer; set: (p: Partial<IconOverlayLayer>) => void }> = ({ o, set }) => (
  <>
    <div className="overlay-inspector-section">
      <span className="overlay-inspector-section-label">Icon</span>
      <div className="overlay-icon-grid">
        {ICON_OPTIONS.map((i) => (
          <button
            key={i.id}
            className={`overlay-icon-btn ${o.icon === i.id ? "active" : ""}`}
            onClick={() => set({ icon: i.id })}
            title={i.label}
            aria-label={i.label}
          >
            {i.glyph}
          </button>
        ))}
      </div>
    </div>
    <div className="overlay-inspector-row">
      <label className="overlay-inspector-label">
        Größe (% Breite)
        <input
          type="number"
          min={1}
          max={80}
          step={0.5}
          value={o.sizePct}
          onChange={(e) => set({ sizePct: Math.max(1, Number(e.target.value)) })}
        />
      </label>
      <label className="overlay-inspector-label">
        Strich
        <input
          type="number"
          min={0}
          max={8}
          step={0.5}
          value={o.strokeWidth ?? 0}
          onChange={(e) => set({ strokeWidth: Math.max(0, Number(e.target.value)) })}
        />
      </label>
    </div>
    <ColorRow value={o.color} onChange={(next) => set({ color: next })} />
  </>
);

const BadgeBody: React.FC<{ o: BadgeOverlayLayer; set: (p: Partial<BadgeOverlayLayer>) => void }> = ({ o, set }) => (
  <>
    <label className="overlay-inspector-label">
      Text
      <input
        type="text"
        value={o.text}
        onChange={(e) => set({ text: e.target.value })}
        placeholder="LIVE"
      />
    </label>
    <div className="overlay-inspector-row">
      <label className="overlay-inspector-label">
        Größe (px)
        <input
          type="number"
          min={8}
          max={200}
          step={2}
          value={o.fontSize}
          onChange={(e) => set({ fontSize: Math.max(8, Number(e.target.value)) })}
        />
      </label>
      <label className="overlay-inspector-label">
        Icon
        <select value={o.icon ?? ""} onChange={(e) => set({ icon: e.target.value || undefined })}>
          <option value="">(keins)</option>
          {ICON_OPTIONS.map((i) => (
            <option key={i.id} value={i.id}>
              {i.glyph} {i.label}
            </option>
          ))}
        </select>
      </label>
    </div>
    <ColorRow value={o.fill} onChange={(next) => set({ fill: next })} label="Füllung" />
    <ColorRow value={o.textColor} onChange={(next) => set({ textColor: next })} label="Textfarbe" />
  </>
);

const ImageBody: React.FC<{ o: ImageOverlayLayer; set: (p: Partial<ImageOverlayLayer>) => void }> = ({ o, set }) => (
  <>
    <label className="overlay-inspector-label">
      URL
      <input
        type="text"
        value={o.src}
        onChange={(e) => set({ src: e.target.value })}
        placeholder="https://…"
      />
    </label>
    <div className="overlay-inspector-row">
      <label className="overlay-inspector-label">
        Breite (%)
        <input
          type="number"
          min={1}
          max={120}
          step={0.5}
          value={o.widthPct}
          onChange={(e) => set({ widthPct: Math.max(1, Number(e.target.value)) })}
        />
      </label>
      <label className="overlay-inspector-label">
        Höhe (%)
        <input
          type="number"
          min={1}
          max={120}
          step={0.5}
          value={o.heightPct}
          onChange={(e) => set({ heightPct: Math.max(1, Number(e.target.value)) })}
        />
      </label>
    </div>
    <div className="overlay-inspector-row">
      <label className="overlay-inspector-label">
        Fit
        <select value={o.fit ?? "cover"} onChange={(e) => set({ fit: e.target.value as "cover" | "contain" })}>
          <option value="cover">cover</option>
          <option value="contain">contain</option>
        </select>
      </label>
      <label className="overlay-inspector-label">
        Radius
        <input
          type="number"
          min={0}
          max={400}
          step={2}
          value={o.radius ?? 0}
          onChange={(e) => set({ radius: Math.max(0, Number(e.target.value)) })}
        />
      </label>
    </div>
  </>
);

/* ============================================================ *
 *  Main inspector                                                *
 * ============================================================ */

const KIND_BADGE: Record<OverlayLayer["kind"], { glyph: string; label: string }> = {
  text: { glyph: "T", label: "Text-Layer" },
  shape: { glyph: "▭", label: "Shape-Layer" },
  icon: { glyph: "✦", label: "Icon-Layer" },
  badge: { glyph: "●", label: "Badge-Layer" },
  image: { glyph: "🖼", label: "Bild-Layer" },
  group: { glyph: "📁", label: "Gruppe" },
};

export const OverlayInspector: React.FC<Props> = ({
  overlay,
  onChange,
  onDelete,
  onDuplicate,
}) => {
  const o = overlay;
  // Kind-specific patch alias — `set` is a thin function that the bodies use to
  // emit a partial of THEIR concrete kind. We cast at the call boundary so
  // the consumer (Composer) sees `Partial<OverlayLayer>` either way.
  const set = (patch: Partial<OverlayLayer>): void => onChange(o.uid, patch);
  const badge = KIND_BADGE[o.kind];

  return (
    <div className="overlay-inspector">
      <div className="overlay-inspector-head">
        <div className="overlay-inspector-badge">
          <span className="overlay-inspector-badge-icon">{badge.glyph}</span>
          <span>{badge.label}</span>
        </div>
        <div className="overlay-inspector-actions">
          {onDuplicate ? (
            <Tooltip title="Duplizieren" shortcut="⌘D">
              <button
                className="btn ghost overlay-inspector-icon-btn"
                onClick={() => onDuplicate(o.uid)}
                data-tip-host="true"
                aria-label="Duplizieren"
              >
                ⎘
              </button>
            </Tooltip>
          ) : null}
          {onDelete ? (
            <Tooltip title="Löschen" shortcut="Del">
              <button
                className="btn ghost danger overlay-inspector-icon-btn"
                onClick={() => onDelete(o.uid)}
                data-tip-host="true"
                aria-label="Löschen"
              >
                🗑
              </button>
            </Tooltip>
          ) : null}
        </div>
      </div>

      {o.kind === "text" ? (
        <TextBody o={o} set={(p) => set(p as Partial<OverlayLayer>)} />
      ) : o.kind === "shape" ? (
        <ShapeBody o={o} set={(p) => set(p as Partial<OverlayLayer>)} />
      ) : o.kind === "icon" ? (
        <IconBody o={o} set={(p) => set(p as Partial<OverlayLayer>)} />
      ) : o.kind === "badge" ? (
        <BadgeBody o={o} set={(p) => set(p as Partial<OverlayLayer>)} />
      ) : o.kind === "image" ? (
        <ImageBody o={o} set={(p) => set(p as Partial<OverlayLayer>)} />
      ) : null}

      <TransformRows o={o} set={set} />
      <TimingRow o={o} set={set} />
    </div>
  );
};
